import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { Post } from '../../types/api';

export const AdminPostsPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [posts, setPosts] = useState<Post[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
    const [search, setSearch] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    const loadPosts = async () => {
        setLoading(true);
        try {
            const res = await api.adminGetPosts(page);
            setPosts(res.data);
            if (res.meta) {
                setTotalPages(res.meta.last_page);
            }
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPosts();
    }, [page]);

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete article permanently?')) return;
        try {
            await api.adminDeletePost(id);
            setFlashMessage({ type: 'success', text: 'Article deleted successfully.' });
            loadPosts();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to delete post.' });
        }
    };

    const filteredPosts = posts.filter(post => {
        if (statusFilter !== 'all' && post.status !== statusFilter) return false;
        if (search.trim()) {
            const q = search.toLowerCase();
            return post.title.toLowerCase().includes(q) || post.slug.toLowerCase().includes(q);
        }
        return true;
    });

    return (
        <div className="space-y-8">
            {/* Top Action Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Articles & Perspectives</h1>
                    <p className="text-sm text-slate-400 mt-1">Write, schedule, and publish editorial articles with SEO controls.</p>
                </div>

                <Link to="/admin/posts/new" className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition">
                    + Write Article
                </Link>
            </div>

            {/* Filter Bar */}
            <div className="p-4 rounded-2xl glass-card flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <button 
                        type="button" 
                        onClick={() => setStatusFilter('all')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${statusFilter === 'all' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'}`}
                    >
                        All
                    </button>
                    <button 
                        type="button" 
                        onClick={() => setStatusFilter('published')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${statusFilter === 'published' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'}`}
                    >
                        Published
                    </button>
                    <button 
                        type="button" 
                        onClick={() => setStatusFilter('draft')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${statusFilter === 'draft' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'}`}
                    >
                        Drafts
                    </button>
                </div>

                <div className="relative max-w-xs w-full">
                    <input 
                        type="text" 
                        value={search} 
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search articles..." 
                        className="w-full px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                    />
                </div>
            </div>

            {/* Articles Table */}
            <div className="p-6 rounded-3xl glass-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wider">
                            <th className="py-4 font-semibold">Article Title</th>
                            <th className="py-4 font-semibold">Category</th>
                            <th className="py-4 font-semibold">Status</th>
                            <th className="py-4 font-semibold">Read Time</th>
                            <th className="py-4 font-semibold">Date</th>
                            <th className="py-4 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {filteredPosts.length > 0 ? (
                            filteredPosts.map(post => (
                                <tr key={post.id} className="hover:bg-slate-800/20">
                                    <td className="py-4">
                                        <span className="font-bold text-white block">{post.title}</span>
                                        <span className="text-xs text-slate-500 font-mono">/blog/{post.slug}</span>
                                    </td>
                                    <td className="py-4 text-xs font-medium text-emerald-400">
                                        {post.category?.name || 'Uncategorized'}
                                    </td>
                                    <td className="py-4">
                                        {post.status === 'published' ? (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                Published
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                                Draft
                                            </span>
                                        )}
                                        {post.featured && (
                                            <span className="ml-1 text-[11px] text-amber-400 font-semibold">★</span>
                                        )}
                                    </td>
                                    <td className="py-4 text-xs text-slate-400">{post.reading_time_minutes} min</td>
                                    <td className="py-4 text-xs text-slate-400">
                                        {new Date(post.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="py-4 text-right space-x-2">
                                        {post.status === 'published' && (
                                            <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded text-xs bg-slate-900 border border-slate-700 text-slate-300 hover:text-white transition">
                                                View
                                            </a>
                                        )}
                                        <Link to={`/admin/posts/${post.id}`} className="px-2.5 py-1 rounded text-xs bg-emerald-950 text-emerald-300 border border-emerald-800 hover:bg-emerald-900 transition">
                                            Edit
                                        </Link>
                                        <button 
                                            type="button" 
                                            onClick={() => handleDelete(post.id)}
                                            className="px-2.5 py-1 rounded text-xs bg-rose-950/60 text-rose-400 border border-rose-800/40 hover:bg-rose-900 transition cursor-pointer"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="py-8 text-center text-slate-500">
                                    {loading ? 'Loading articles...' : 'No articles found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-end gap-2 text-xs">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <span className="text-slate-400">Page {page} of {totalPages}</span>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
